import React from 'react';
import {
  Input,
  Select,
  Checkbox,
  FormSection,
  FormActions,
} from '../../../design-system/components';
import { useInertiaForm } from '../../../hooks';
import { admin_campaigns_path } from '../../../lib/routes';
const COMMISSION_TYPES = [
  {
    value: 'percentage',
    label: 'Percentage',
  },
  {
    value: 'flat',
    label: 'Flat Amount',
  },
];
const DURATION_TYPES = [
  {
    value: 'one_time',
    label: 'One-time',
  },
  {
    value: 'recurring',
    label: 'Recurring (lifetime)',
  },
  {
    value: 'recurring_capped',
    label: 'Recurring (capped)',
  },
];
const ATTRIBUTION_MODELS = [
  {
    value: 'first_touch',
    label: 'First Touch',
  },
  {
    value: 'last_touch',
    label: 'Last Touch',
  },
];
const COUPON_DISCOUNT_TYPES = [
  {
    value: 'percent_off',
    label: 'Percent Off',
  },
  {
    value: 'amount_off',
    label: 'Amount Off',
  },
];
export default function CampaignForm({ campaign, action, method }) {
  const form = useInertiaForm({
    name: campaign.name || '',
    commission_type: campaign.commission_type || 'percentage',
    commission_rate: campaign.commission_rate ? parseFloat(campaign.commission_rate) : 0,
    commission_duration_type: campaign.commission_duration_type || 'recurring',
    commission_duration_months: campaign.commission_duration_months ?? '',
    cookie_window_days: campaign.cookie_window_days || 60,
    attribution_model: campaign.attribution_model || 'first_touch',
    min_payout_dollars: campaign.min_payout_cents
      ? String(parseFloat((campaign.min_payout_cents / 100).toFixed(2)))
      : '0',
    payout_hold_days: campaign.payout_hold_days || 0,
    payout_hold_cycles: campaign.payout_hold_cycles || 0,
    coupon_enabled: campaign.coupon_enabled || false,
    default_coupon_discount_type: campaign.default_coupon_discount_type || 'percent_off',
    default_coupon_discount_value: campaign.default_coupon_discount_value != null
      ? String(parseFloat(campaign.default_coupon_discount_value))
      : '',
    is_default: campaign.is_default || false,
    auto_payout: campaign.auto_payout || false,
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    if (method === 'post') {
      form.post(action);
    } else {
      form.patch(action);
    }
  };
  const onChange = (e) => {
    form.setData(e.target.name, e.target.value);
  };
  const rateHint =
    form.data.commission_type === 'percentage'
      ? 'Percentage of sale amount'
      : 'Fixed dollar amount per referral';
  const rateSuffix = form.data.commission_type === 'percentage' ? '%' : '$';
  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <FormSection heading="Basic Info" description="The campaign name is visible to affiliates.">
        <div className="max-w-md">
          <Input
            label="Campaign Name"
            name="name"
            value={form.data.name}
            onChange={onChange}
            error={form.getError('name')}
            required
          />
        </div>
      </FormSection>

      <FormSection
        heading="Commission Structure"
        description="Define how affiliates earn commissions from this campaign."
      >
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Select
            label="Commission Type"
            name="commission_type"
            value={form.data.commission_type}
            onChange={onChange}
            error={form.getError('commission_type')}
          >
            {COMMISSION_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Input
            label="Commission Rate"
            name="commission_rate"
            type="number"
            step={form.data.commission_type === 'percentage' ? '0.01' : '1'}
            min="0"
            value={form.data.commission_rate}
            onChange={onChange}
            suffix={rateSuffix}
            helperText={rateHint}
            error={form.getError('commission_rate')}
            required
          />
          <Select
            label="Duration Type"
            name="commission_duration_type"
            value={form.data.commission_duration_type}
            onChange={onChange}
            error={form.getError('commission_duration_type')}
          >
            {DURATION_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {form.data.commission_duration_type === 'recurring_capped' && (
            <Input
              label="Duration (months)"
              name="commission_duration_months"
              type="number"
              min="1"
              value={form.data.commission_duration_months}
              onChange={onChange}
              error={form.getError('commission_duration_months')}
              required
            />
          )}
          <Input
            label="Minimum Payout"
            name="min_payout_dollars"
            type="number"
            step="0.01"
            min="0"
            prefix="$"
            helperText="Minimum amount before payout is issued"
            value={form.data.min_payout_dollars}
            onChange={onChange}
            error={form.getError('min_payout_dollars')}
          />
        </div>
      </FormSection>

      <FormSection
        heading="Payout Hold"
        description="Delay payouts to protect against chargebacks and fraud."
      >
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Input
            label="Hold Days"
            name="payout_hold_days"
            type="number"
            min="0"
            helperText="Days to hold commissions before payout"
            value={form.data.payout_hold_days}
            onChange={onChange}
            error={form.getError('payout_hold_days')}
          />
          <Input
            label="Hold Cycles"
            name="payout_hold_cycles"
            type="number"
            min="0"
            helperText="Billing cycles to hold before payout"
            value={form.data.payout_hold_cycles}
            onChange={onChange}
            error={form.getError('payout_hold_cycles')}
          />
        </div>
      </FormSection>

      <FormSection
        heading="Tracking Settings"
        description="Configure how referrals are tracked and attributed."
      >
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Input
            label="Cookie Window (days)"
            name="cookie_window_days"
            type="number"
            min="1"
            helperText="How long the referral cookie lasts"
            value={form.data.cookie_window_days}
            onChange={onChange}
            error={form.getError('cookie_window_days')}
          />
          <Select
            label="Attribution Model"
            name="attribution_model"
            value={form.data.attribution_model}
            onChange={onChange}
            error={form.getError('attribution_model')}
          >
            {ATTRIBUTION_MODELS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </FormSection>

      <FormSection
        heading="Coupon Settings"
        description="Optionally enable coupon codes for affiliates in this campaign."
      >
        <div className="max-w-2xl space-y-4">
          <Checkbox
            label="Enable Coupons"
            description="Allow affiliates to create coupon codes for referrals"
            checked={form.data.coupon_enabled}
            onChange={(e) => form.setData('coupon_enabled', e.target.checked)}
          />
          {form.data.coupon_enabled && (
            <div className="grid gap-4 sm:grid-cols-2 pl-6 border-l-2 border-edge-strong">
              <Select
                label="Discount Type"
                name="default_coupon_discount_type"
                value={form.data.default_coupon_discount_type}
                onChange={onChange}
                error={form.getError('default_coupon_discount_type')}
              >
                {COUPON_DISCOUNT_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <Input
                label="Discount Value"
                name="default_coupon_discount_value"
                type="number"
                step="0.01"
                min="0"
                suffix={form.data.default_coupon_discount_type === 'percent_off' ? '%' : '$'}
                value={form.data.default_coupon_discount_value}
                onChange={onChange}
                error={form.getError('default_coupon_discount_value')}
              />
            </div>
          )}
        </div>
      </FormSection>

      <FormSection heading="Options" description="Additional campaign settings.">
        <div className="space-y-4">
          <Checkbox
            label="Default Campaign"
            description="New affiliates will be assigned to this campaign by default. Only one campaign can be the default."
            checked={form.data.is_default}
            onChange={(e) => form.setData('is_default', e.target.checked)}
          />
          <Checkbox
            label="Auto-Payout via PayPal"
            description="Automatically pay all affiliates in this campaign via PayPal on a daily schedule."
            checked={form.data.auto_payout}
            onChange={(e) => form.setData('auto_payout', e.target.checked)}
          />
        </div>
      </FormSection>

      <FormActions
        submitLabel={campaign.id ? 'Update Campaign' : 'Create Campaign'}
        processing={form.processing}
        cancelHref={admin_campaigns_path()}
      />
    </form>
  );
}
